import { normalizeSettingsSearchQuery } from './settings-search';
import {
  archivedChatSearchId,
  integrationSearchId,
  mcpClientSearchId,
  shortcutSearchId,
} from './settings-search-dom';
import { settingsSearchEntries } from './settings-search.config';

/**
 * Remembers which settings row the user picked for a query (an "acceptance")
 * so later, similar searches can rank that row above equally-scored ones.
 * Acceptances live only for the current browser/app session so potentially
 * sensitive search terms are not retained across sessions. They stay
 * bounded: each one's weight
 * decays with a ~30-day half-life, the store caps at 50 entries (weakest /
 * oldest pruned first), and rows whose entry ids no longer resolve are
 * dropped on load. The boost is a tiebreaker only — `searchSettings` applies
 * it exclusively between entries with identical fuzzy scores
 */

export interface SettingsSearchAcceptance {
  /** Normalized query the user typed when they picked the entry */
  query: string;
  entryId: string;
  /** Accumulated acceptance count, decayed with a ~30-day half-life */
  weight: number;
  /** Time of the last acceptance (ms epoch); decay is measured from here */
  acceptedAt: number;
}

export const ACCEPTANCE_STORAGE_KEY = 'settings-search-acceptances';
export const ACCEPTANCE_HALF_LIFE_MS = 30 * 24 * 60 * 60 * 1000;
export const MAX_STORED_ACCEPTANCES = 50;
// ~4 half-lives old: too faded to ever win a tie, so not worth keeping
const MIN_STORED_WEIGHT = 0.05;

// `integrations.` also covers `integrations.mcp.` ids
const PROVIDER_ID_PREFIXES = [
  shortcutSearchId(''),
  integrationSearchId(''),
  mcpClientSearchId(''),
  archivedChatSearchId(''),
];

let staticEntryIds: Set<string> | null = null;

/**
 * A static manifest id, or the shape of a provider-generated id. Provider
 * rows are dynamic (a chat can be unarchived, an integration disconnected),
 * so only the id shape is checked — a stale provider id just never matches a
 * rendered result and decays away
 */
export const isKnownSettingsEntryId = (entryId: string): boolean => {
  staticEntryIds ??= new Set(settingsSearchEntries.map((entry) => entry.id));
  return (
    staticEntryIds.has(entryId) ||
    PROVIDER_ID_PREFIXES.some(
      (prefix) => entryId.length > prefix.length && entryId.startsWith(prefix)
    )
  );
};

export interface SettingsSearchAcceptanceDeps {
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  now: () => number;
  isKnownEntryId: (entryId: string) => boolean;
}

const defaultDeps = (): SettingsSearchAcceptanceDeps => {
  return {
    // Resolve `window.sessionStorage` per operation, not here: with storage
    // disabled the property access itself throws a SecurityError, and default
    // arguments evaluate before the callers' try blocks can catch it.
    storage: {
      getItem: (key) => window.sessionStorage.getItem(key),
      setItem: (key, value) => window.sessionStorage.setItem(key, value),
    },
    now: () => Date.now(),
    isKnownEntryId: isKnownSettingsEntryId,
  };
};

export const decayedAcceptanceWeight = (
  acceptance: SettingsSearchAcceptance,
  now: number
): number =>
  acceptance.weight *
  // clamp so a clock-skewed future `acceptedAt` can't inflate the weight
  Math.pow(
    0.5,
    Math.max(0, now - acceptance.acceptedAt) / ACCEPTANCE_HALF_LIFE_MS
  );

const isAcceptanceShaped = (
  value: unknown
): value is SettingsSearchAcceptance => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.query === 'string' &&
    typeof candidate.entryId === 'string' &&
    typeof candidate.weight === 'number' &&
    Number.isFinite(candidate.weight) &&
    typeof candidate.acceptedAt === 'number' &&
    Number.isFinite(candidate.acceptedAt)
  );
};

/** Corrupt JSON, malformed rows, unknown ids, and fully-decayed rows all load as absent */
const loadAcceptances = ({
  storage,
  now,
  isKnownEntryId,
}: SettingsSearchAcceptanceDeps): SettingsSearchAcceptance[] => {
  let parsed: unknown;
  try {
    const raw = storage.getItem(ACCEPTANCE_STORAGE_KEY);
    if (!raw) return [];
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter(isAcceptanceShaped)
    .filter(
      (acceptance) =>
        isKnownEntryId(acceptance.entryId) &&
        decayedAcceptanceWeight(acceptance, now()) >= MIN_STORED_WEIGHT
    );
};

/**
 * Pure core of `recordSettingsSearchAcceptance`: re-accepting the same
 * (query, entry) pair folds its decayed weight into a fresh acceptance, and
 * the result keeps only the strongest `MAX_STORED_ACCEPTANCES` rows —
 * weakest (by decayed weight), then oldest, pruned first
 */
export const recordAcceptance = (
  acceptances: readonly SettingsSearchAcceptance[],
  query: string,
  entryId: string,
  now: number
): SettingsSearchAcceptance[] => {
  const normalizedQuery = normalizeSettingsSearchQuery(query);
  if (!normalizedQuery) return [...acceptances];

  const existing = acceptances.find(
    (acceptance) =>
      acceptance.query === normalizedQuery && acceptance.entryId === entryId
  );
  const updated: SettingsSearchAcceptance = {
    query: normalizedQuery,
    entryId,
    weight: existing ? decayedAcceptanceWeight(existing, now) + 1 : 1,
    acceptedAt: now,
  };
  return [
    ...acceptances.filter((acceptance) => acceptance !== existing),
    updated,
  ]
    .sort(
      (a, b) =>
        decayedAcceptanceWeight(b, now) - decayedAcceptanceWeight(a, now) ||
        b.acceptedAt - a.acceptedAt
    )
    .slice(0, MAX_STORED_ACCEPTANCES);
};

// 1-2 char prefixes stay exact-only — mirrors MIN_FUZZY_QUERY_LENGTH in
// settings-search.ts; a query like "a" would otherwise collect boosts from
// every recorded query starting with "a"
const MIN_PREFIX_MATCH_LENGTH = 3;

/**
 * Query matching is deliberately simple and predictable: an acceptance
 * applies when its recorded query equals the current normalized query, or
 * one is a prefix of the other — covering both typing toward a past query
 * ("dar" → recorded "dark") and extending it ("dark mode" → recorded "dark").
 * Prefix matching needs the shared prefix (the shorter query) to be at least
 * `MIN_PREFIX_MATCH_LENGTH` chars; shorter queries only match exactly
 */
const queriesMatch = (recorded: string, current: string): boolean => {
  if (recorded === current) return true;
  if (Math.min(recorded.length, current.length) < MIN_PREFIX_MATCH_LENGTH) {
    return false;
  }
  return recorded.startsWith(current) || current.startsWith(recorded);
};

/** Pure core of `getSettingsSearchBoosts`: entry id → summed decayed weight */
export const acceptanceBoosts = (
  acceptances: readonly SettingsSearchAcceptance[],
  query: string,
  now: number
): Map<string, number> => {
  const boosts = new Map<string, number>();
  const normalizedQuery = normalizeSettingsSearchQuery(query);
  if (!normalizedQuery) return boosts;

  for (const acceptance of acceptances) {
    if (!queriesMatch(acceptance.query, normalizedQuery)) continue;
    const weight = decayedAcceptanceWeight(acceptance, now);
    boosts.set(
      acceptance.entryId,
      (boosts.get(acceptance.entryId) ?? 0) + weight
    );
  }
  return boosts;
};

/**
 * Records that the user picked `entryId` for `query`. Best-effort: quota or
 * disabled-storage failures are swallowed — search works without the boost
 */
export const recordSettingsSearchAcceptance = (
  query: string,
  entryId: string,
  deps: SettingsSearchAcceptanceDeps = defaultDeps()
): void => {
  try {
    const updated = recordAcceptance(
      loadAcceptances(deps),
      query,
      entryId,
      deps.now()
    );
    deps.storage.setItem(ACCEPTANCE_STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // storage unavailable or over quota: skip recording
  }
};

/** Tiebreak boosts for `searchSettings`, from acceptances similar to `query` */
export const getSettingsSearchBoosts = (
  query: string,
  deps: SettingsSearchAcceptanceDeps = defaultDeps()
): Map<string, number> => {
  try {
    return acceptanceBoosts(loadAcceptances(deps), query, deps.now());
  } catch {
    return new Map();
  }
};
